import {Account} from '../../components/Search/SearchNav/types';
import {
  BillingPeriodSubscriptionsQuery,
  InferenceCostUsageAggregation,
  TrainingCostUsageAggregation,
  UsageAggregation,
  UsageType,
  useUsageTotalInfoQuery,
} from '../../generated/graphql';
import {LimitDateInterval} from '../accounts/pricing';
import {addMonthsUTC, startOfDayUTC, startOfMonthUTC} from '../date';

export type WithRawUsageTotal = {usageTotal?: any[]} | null | undefined;
export type UsageTotal = number[] | undefined;
export function getNormalizedUsageTotal(
  withRawUsageTotal: WithRawUsageTotal,
  usageType: UsageType
): UsageTotal {
  const rawUsageTotal = withRawUsageTotal?.usageTotal;
  if (rawUsageTotal == null) {
    return undefined;
  }
  // If the usage total is an array, we assume it's a single value in the array
  // and convert it to a BigInt.
  return rawUsageTotal.map(rawValue =>
    normalizeUsageTotalBigInt(BigInt(rawValue), usageType)
  );
}

export function normalizeUsageTotalBigInt(
  rawValue: bigint,
  usageType: UsageType
): number {
  rawValue = BigInt(rawValue); // Ensure rawValue is a BigInt
  switch (usageType) {
    case UsageType.InferenceCost:
      // Convert from billionth of cents to cents
      return Number(rawValue / BigInt(1_000_000_000));
    case UsageType.Storage:
    case UsageType.Weave:
    case UsageType.TrackedHours:
    default:
      return Number(rawValue);
  }
}

export type UsageAggregationTypes =
  | UsageAggregation
  | InferenceCostUsageAggregation
  | TrainingCostUsageAggregation;

export function useGetUsageThisBillingPeriod(
  account: Account | null,
  usageType: UsageType,
  intervalMarkers: Date[],
  skip: boolean
) {
  const {data, loading, error} = useUsageTotalInfoQuery({
    variables: {
      orgName: account?.name ?? '',
      usageType,
      intervalMarkers: intervalMarkers.map(marker => marker.toISOString()),
    },
    skip:
      skip ||
      account?.name == null ||
      !areValidIntervalMarkers(intervalMarkers), // skip if the interval markers are out of order (i.e. the subscription started today)
  });
  const usage =
    getNormalizedUsageTotal(data?.organization, usageType)?.reduce(
      (acc, val) => acc + val,
      0
    ) ?? 0;

  return {
    usage,
    loading,
    error,
  };
}

export function areValidIntervalMarkers(dates: Date[]): boolean {
  if (dates.length < 2) {
    return false; // Need at least two dates to form a valid interval
  }
  for (let i = 1; i < dates.length; i++) {
    if (dates[i - 1].getTime() > dates[i].getTime()) {
      return false;
    }
  }
  return true;
}

type BillingPeriodOrganization = NonNullable<
  BillingPeriodSubscriptionsQuery['organization']
>;
type BillingPeriodSubscription = Pick<
  BillingPeriodOrganization['subscriptions'][number],
  'billingPeriodStart' | 'billingPeriodEnd'
>;

export function getIntervalMarkersFromSubscription(
  subscription: BillingPeriodSubscription | null | undefined
): Date[] | null {
  if (
    subscription?.billingPeriodEnd == null ||
    subscription?.billingPeriodStart == null
  ) {
    return null;
  }
  const billingPeriodStart = startOfDayUTC(
    new Date(subscription.billingPeriodStart)
  );
  const billingPeriodEnd = startOfDayUTC(
    new Date(subscription.billingPeriodEnd)
  );
  return [billingPeriodStart, billingPeriodEnd];
}

export function getIntervalMarkersFromCurrentCalendarMonth(): Date[] {
  const today = startOfDayUTC(new Date());
  return [startOfMonthUTC(today), addMonthsUTC(startOfMonthUTC(today), 1)];
}

export function getIntervalMarkersFromLimitDateInterval(
  limitDateInterval: LimitDateInterval
): Date[] {
  const billingPeriodStart = startOfDayUTC(new Date(limitDateInterval.start));
  const billingPeriodEnd = startOfDayUTC(new Date(limitDateInterval.end));
  return [billingPeriodStart, billingPeriodEnd];
}
